import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/PaymentModal.css';
import { getCurrentUser } from '../../utils/auth';
import { isIsraelUser } from '../../utils/locationUtils';
import { normalizeOrderType } from '../../utils/orderTypeUtils';
import { getCurrentTimestamp, formatCurrency } from '../../utils/dateUtils';
import { API_URL } from '../../config/api';
import * as api from '../../services/api';
import WarrantyConfirmationModal from './WarrantyConfirmationModal';
import { CLOVER_ALLOWED_USERS } from '../../utils/userUtils';

const PaymentModal = ({ orderNo, currentBalance, onClose, onPaymentSuccess, taxInfo, customerId }) => {
  // Remove this line - it's causing the conflict
  // const currentUserData = getCurrentUser();

  // Helper function to get full user data
  const getCurrentUserData = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      console.error('Error getting user data:', error);
      return {};
    }
  };

  // Get the data we need
  const currentUsername = getCurrentUser(); // Returns "Noga"
  const currentUserData = getCurrentUserData(); // Returns full user object
  const isUserInIsrael = isIsraelUser(currentUserData);

  const [paymentData, setPaymentData] = useState({
    method: 'Cash',
    amount: '',
    notes: '',
    cardLast4: '',
    checkNumber: ''
  });

  // 🆕 CLOVER: Saved cards state
  const [cloverCards, setCloverCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const selectedCard = cloverCards.find(c => c.PaymentMethodId === selectedCardId) || null;
  const isSelectedCardDebit = selectedCard?.IsDebit === 1 || selectedCard?.IsDebit === true;
  const isCloverEnabled = process.env.REACT_APP_CLOVER_ENABLED === 'true';

  // 🔒 FEATURE FLAG: Limit Clover card-on-file to specific users during rollout
  const canAccessCloverFeature = isCloverEnabled &&
    process.env.REACT_APP_ENABLE_CARD_ON_FILE === 'true' &&
    CLOVER_ALLOWED_USERS.includes(currentUsername?.toLowerCase());

  // 🆕 CLOVER: Debug logging (only on mount and when key values change)
  useEffect(() => {
    console.log('💳 CLOVER DEBUG:', {
      isCloverEnabled,
      customerId,
      cloverCardsCount: cloverCards.length,
      selectedCardId,
      envVar: process.env.REACT_APP_CLOVER_ENABLED
    });
  }, [isCloverEnabled, customerId, cloverCards.length, selectedCardId]);

  // 🆕 NEW: Tax-related state - now initialize from props
  const [orderTaxInfo, setOrderTaxInfo] = useState({
    isTaxable: false,
    taxRate: 0,
    originalTaxAmount: 0,
    paidTaxAmount: 0,
    remainingTaxBalance: 0,
    remainingBaseBalance: 0,
    originalOrderType: null,
    loading: true,
    error: null
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [warrantyModalData, setWarrantyModalData] = useState(null);

  const paymentMethods = [
    'Cash',
    'Credit Card', 
    'Debit Card',
    'Check',
    'Zelle',
    'Venmo',
    'Bank Transfer'
  ];

  // 🆕 CLOVER: Fetch saved cards when modal opens
  useEffect(() => {
    const fetchSavedCards = async () => {
      if (!customerId || !isCloverEnabled) {
        console.log('Skipping Clover fetch:', { customerId, isCloverEnabled });
        return;
      }

      try {
        console.log(`💳 Fetching saved cards for customer ${customerId}...`);
        const response = await axios.get(`${API_URL}/api/clover/customer/${customerId}/cards`);

        if (response.data.success) {
          setCloverCards(response.data.cards || []);
          console.log(`✅ Loaded ${response.data.count} saved card(s)`);

        }
      } catch (error) {
        console.error('❌ Error fetching saved cards:', error);
        // Silently fail - user can still use manual entry or other payment methods
      }
    };

    fetchSavedCards();
  }, [customerId, isCloverEnabled]);

  // Fetch work order data for warranty modal
  useEffect(() => {
    const fetchWorkOrder = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/orders/order/${orderNo}`);
        setWorkOrder(response.data);
        console.log('📋 Work order data loaded:', response.data);
      } catch (error) {
        console.error('⚠️ Error fetching work order:', error);
      }
    };

    if (orderNo) {
      fetchWorkOrder();
    }
  }, [orderNo]);

  // 🆕 NEW: Initialize tax info from props or fetch from API
  useEffect(() => {
    if (taxInfo) {
      // Use tax info passed from parent component
      console.log('🔍 Using tax info from parent component:', taxInfo);
      setOrderTaxInfo({
        isTaxable: taxInfo.isTaxable,
        taxRate: taxInfo.taxRate || 0,
        originalTaxAmount: taxInfo.originalTaxAmount || 0,
        paidTaxAmount: taxInfo.paidTaxAmount || 0,
        remainingTaxBalance: taxInfo.remainingTaxBalance || 0,
        remainingBaseBalance: parseFloat(currentBalance || 0) - (taxInfo.remainingTaxBalance || 0),
        originalOrderType: taxInfo.orderType,
        loading: false,
        error: null
      });
    } else {
      // Fallback to API call if no tax info provided
      fetchOrderTaxInfo();
    }
  }, [orderNo, taxInfo, currentBalance]);

  const fetchOrderTaxInfo = async () => {
    try {      
      const warrantyResponse = await axios.get(`${API_URL}/api/orders/${orderNo}/warranty`);
      const warrantyData = warrantyResponse.data;
      
      
      // 🔧 FIXED: Extract OrderWigType from warranty response (now always included)
      const orderType = warrantyData.OrderWigType || warrantyData.orderWigType;
      
      if (!orderType) {
        throw new Error('OrderWigType not found in warranty response');
      }
      
      // 🔧 NEW: Fetch order details for payment and tax info
      const orderResponse = await axios.get(`${API_URL}/api/orders/order/${orderNo}`);

      const order = orderResponse.data;
      
      // "Aidel New Wig" follows the same tax rules as "New Wig".
      const isTaxable = ['New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderType));
      
      console.log('🔍 Order type taxability check:', {
        orderType: orderType,
        sourceEndpoint: 'warranty',
        isTaxable: isTaxable,
        taxableTypes: ['New Wig', 'New Sale Wig']
      });
      
      if (!isTaxable) {
        setOrderTaxInfo({
          isTaxable: false,
          taxRate: 0,
          originalTaxAmount: 0,
          paidTaxAmount: 0,
          remainingTaxBalance: 0,
          remainingBaseBalance: parseFloat(currentBalance || 0),
          originalOrderType: orderType,
          loading: false,
          error: null
        });
        return;
      }
      
      if (order.orderItems && order.orderItems.length > 0) {
        order.orderItems.forEach((item, index) => {
          console.log(`Item ${index + 1}:`, {
            id: item.id,
            description: item.description,
            price: item.price,
            extendedCost: item.extendedCost,
            quantity: item.quantity,
            fullItem: item
          });
        });
      }
      
      // Extract tax rate from order items (look for "State Tax Applied" line)
      const taxLineItem = order.orderItems?.find(item => 
        item.description && (
          item.description.toLowerCase().includes('state tax applied') ||
          item.description.toLowerCase().includes('tax applied') ||
          item.description.toLowerCase().includes('sales tax') ||
          item.description.toLowerCase().includes('tax:') ||
          item.description.includes('%')  // Any item with % symbol
        )
      );
      
      let taxRate = 0;
      let originalTaxAmount = 0;
      
      if (taxLineItem) {
        
        // Extract tax rate from description - try multiple patterns
        let taxRateMatch = taxLineItem.description.match(/(\d+\.?\d*)%/);
        
        if (!taxRateMatch) {
          // Try alternative patterns like "8.375" without %
          taxRateMatch = taxLineItem.description.match(/(\d+\.\d+)/);
        }
        
        if (taxRateMatch) {
          taxRate = parseFloat(taxRateMatch[1]);
        } else {
          console.log('❌ Could not extract tax rate from:', taxLineItem.description);
        }
        
        originalTaxAmount = parseFloat(taxLineItem.price || taxLineItem.extendedCost || 0);
        console.log('✅ Original tax amount:', originalTaxAmount);
      } else {
        console.log('❌ No tax line item found in order items');
        
        // Fallback: Try to get tax rate from order.totals if available
        if (order.totals && order.totals.taxRate > 0) {
          taxRate = order.totals.taxRate;
          originalTaxAmount = order.totals.tax || 0;
          console.log('✅ Using tax rate from order.totals:', taxRate + '%');
        } else {
          console.log('❌ No tax rate found in order.totals either');
        }
      }
      
      // Calculate how much tax has been paid
      const taxPayments = order.payments?.filter(payment => 
        payment.notes && payment.notes.toLowerCase().includes('state tax applied')
      ) || [];
      
      const paidTaxAmount = taxPayments.reduce((sum, payment) => 
        sum + parseFloat(payment.amount || 0), 0
      );
      
      // Calculate remaining balances
      const remainingTaxBalance = Math.max(0, originalTaxAmount - paidTaxAmount);
      const totalBalance = parseFloat(currentBalance || 0);
      const remainingBaseBalance = Math.max(0, totalBalance - remainingTaxBalance);
      
      console.log('💰 Tax calculation results:', {
        isTaxable,
        taxRate,
        originalTaxAmount,
        paidTaxAmount,
        remainingTaxBalance,
        remainingBaseBalance,
        totalBalance,
        orderTypeSource: 'warranty_endpoint'
      });
      
      const finalTaxInfo = {
        isTaxable,
        taxRate,
        originalTaxAmount,
        paidTaxAmount,
        remainingTaxBalance,
        remainingBaseBalance,
        originalOrderType: orderType,
        loading: false,
        error: null
      };
      
      console.log('📝 Setting orderTaxInfo state to:', finalTaxInfo);
      
      setOrderTaxInfo(finalTaxInfo);
      
    } catch (error) {
      console.error('❌ Failed to fetch order tax info:', error);
      
      // 🔧 ENHANCED: More specific error handling
      let errorMessage = 'Failed to load tax information';
      if (error.response?.status === 404) {
        errorMessage = 'Order not found';
      } else if (error.message.includes('warranty')) {
        errorMessage = 'Could not fetch order type information';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error - please try again';
      }
      
      setOrderTaxInfo(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
    }
  };

  // 🆕 NEW: Calculate tax allocation for payment
  // IMPORTANT: User enters TOTAL amount (which already includes tax from the receipt)
  // We need to EXTRACT the tax portion, not add more tax on top
  const calculatePaymentTaxAllocation = (paymentAmount) => {
    console.log('🧮 calculatePaymentTaxAllocation called with:', {
      paymentAmount,
      orderTaxInfo: orderTaxInfo,
      isTaxable: orderTaxInfo.isTaxable,
      remainingTaxBalance: orderTaxInfo.remainingTaxBalance
    });

    if (!orderTaxInfo.isTaxable || !paymentAmount || orderTaxInfo.remainingTaxBalance <= 0) {
      console.log('❌ No tax allocation needed:', {
        isTaxable: orderTaxInfo.isTaxable,
        hasPaymentAmount: !!paymentAmount,
        remainingTaxBalance: orderTaxInfo.remainingTaxBalance
      });

      return {
        baseAmount: parseFloat(paymentAmount),
        taxOnPayment: 0,
        hasTaxAllocation: false
      };
    }

    const totalPayment = parseFloat(paymentAmount);

    // FIXED: User enters the TOTAL (subtotal + tax already included)
    // We need to EXTRACT the tax portion from the total, not add tax on top
    // Formula: If total = base + (base * taxRate), then base = total / (1 + taxRate)

    const taxRateDecimal = orderTaxInfo.taxRate / 100;

    // Calculate how much of this payment is base vs tax
    // baseAmount = totalPayment / (1 + taxRate)
    // taxOnPayment = totalPayment - baseAmount
    let baseAmount = totalPayment / (1 + taxRateDecimal);
    let taxOnPayment = totalPayment - baseAmount;

    // Round to 2 decimal places
    baseAmount = Math.round(baseAmount * 100) / 100;
    taxOnPayment = Math.round(taxOnPayment * 100) / 100;

    // Cap tax at remaining tax balance
    if (taxOnPayment > orderTaxInfo.remainingTaxBalance) {
      taxOnPayment = orderTaxInfo.remainingTaxBalance;
      // Recalculate base amount: base = total - capped tax
      baseAmount = Math.round((totalPayment - taxOnPayment) * 100) / 100;
    }

    console.log('💰 Tax EXTRACTION calculation (fixed):', {
      userEnteredTotal: totalPayment,
      taxRate: orderTaxInfo.taxRate + '%',
      extractedBaseAmount: baseAmount,
      extractedTaxPortion: taxOnPayment,
      remainingTaxBalance: orderTaxInfo.remainingTaxBalance,
      verification: baseAmount + taxOnPayment + ' should equal ' + totalPayment
    });

    const result = {
      baseAmount: baseAmount,
      taxOnPayment: taxOnPayment,
      hasTaxAllocation: taxOnPayment > 0
    };

    console.log('✅ Tax allocation result:', result);
    return result;
  };

  // Calculate credit card fee (3% for payments over $150, Credit Card only, non-Israeli users)
  const calculateCardFee = (amount, method) => {
    if ((method === 'Credit Card') && parseFloat(amount) >= 150 && !isUserInIsrael) {
      return parseFloat(amount) * 0.03;
    }
    return 0;
  };

  // 🔧 ENHANCED: Calculate totals with tax allocation
  // User enters the TOTAL amount (which already includes tax)
  const userEnteredAmount = parseFloat(paymentData.amount) || 0;
  const taxAllocation = calculatePaymentTaxAllocation(userEnteredAmount);
  const cardFee = isSelectedCardDebit ? 0 : calculateCardFee(userEnteredAmount, paymentData.method);

  // Total charge = user entered amount + card fee only
  // (user already entered the amount including tax, so we don't add tax again)
  const totalCharge = userEnteredAmount + cardFee;

  // For display purposes, keep baseAmount as the extracted base (without tax)
  const baseAmount = taxAllocation.baseAmount;

//  const formatCurrency = (amount) => {
//     const num = parseFloat(amount || 0);
//     return new Intl.NumberFormat('en-US', {
//       style: 'currency',
//       currency: 'USD',
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     }).format(num);
//   };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      // Validate payment amount
      const amount = parseFloat(paymentData.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid payment amount');
      }

      // 🆕 CLOVER: Handle saved card charge
      if (selectedCardId) {
        // Calculate the 3% fee — debit cards are exempt
        const savedCardFee = isSelectedCardDebit ? 0 : calculateCardFee(amount, 'Credit Card');
        const totalChargeWithFee = parseFloat((amount + savedCardFee).toFixed(2));

        console.log(`💳 Charging saved card ${selectedCardId} for $${totalChargeWithFee} (base $${amount} + fee $${savedCardFee}, debit=${isSelectedCardDebit})...`);

        const chargeResponse = await axios.post(`${API_URL}/api/clover/charge-card`, {
          paymentMethodId: selectedCardId,
          amount: totalChargeWithFee,
          orderNo: orderNo,
          username: currentUsername
        });

        if (!chargeResponse.data.success) {
          throw new Error(chargeResponse.data.error || 'Failed to charge saved card');
        }

        console.log('✅ Saved card charged successfully:', chargeResponse.data);

        console.log('📤 Sending payment record to n8n webhook for DB...');

        // Calculate tax allocation for the payment
        const taxAllocation = calculatePaymentTaxAllocation(amount);
        const cardFee = savedCardFee;

        const payload = {
          orderNo: orderNo,
          paymentMethod: isSelectedCardDebit ? 'Debit Card' : 'Credit Card',
          user: currentUsername || 'DiniV2System',
          isIsraelUser: isUserInIsrael,
          userLocation: currentUserData?.UserLocation || 'Unknown',

          // Payment amounts
          amount: taxAllocation.baseAmount,
          baseAmount: taxAllocation.baseAmount,
          taxOnPayment: taxAllocation.taxOnPayment,
          hasTaxAllocation: taxAllocation.hasTaxAllocation,
          cardFee: cardFee,
          totalCharge: taxAllocation.baseAmount + taxAllocation.taxOnPayment + cardFee,

          // Other data
          notes: paymentData.notes || null,
          cardLast4: chargeResponse.data.transaction.card.lastFour,
          currentBalance: currentBalance,
          timestamp: getCurrentTimestamp(),

          // Business rules
          businessRules: {
            feeApplied: false,
            feeRate: 0,
            feeThreshold: 150,
            isIsraelUser: isUserInIsrael,
            requiresQuickBooks: false,
            processingRegion: isUserInIsrael ? 'Israel' : 'International',
            taxAllocationApplied: taxAllocation.hasTaxAllocation,
            taxRate: orderTaxInfo.taxRate,
            originalOrderType: orderTaxInfo.originalOrderType,
            remainingTaxBalance: orderTaxInfo.remainingTaxBalance,
            taxCalculationMethod: 'proportional_to_payment',
            orderTypeSource: 'warranty_endpoint',
            cloverTransaction: true,
            cloverTransactionId: chargeResponse.data.transaction.transactionId
          }
        };

        // Get n8n webhook URL
        const getN8nWebhookUrl = () => {
          if (typeof window === 'undefined') return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging/webhook/payment-processing';
          const hostname = window.location.hostname;
          if (hostname === 'dinitracker.com' || hostname === 'dinitracker-v2-prod.pages.dev') {
            return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/webhook/prod-payment-processing';
          }
          return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging/webhook/payment-processing';
        };

        const N8N_WEBHOOK_URL = getN8nWebhookUrl();
        console.log('🚀 Sending to n8n webhook:', N8N_WEBHOOK_URL);

        try {
          const webhookResponse = await axios.post(N8N_WEBHOOK_URL, payload, {
            timeout: 30000,
            headers: { 'Content-Type': 'application/json' }
          });
          console.log('✅ Payment recorded in DB:', webhookResponse.data);
        } catch (webhookError) {
          console.error('⚠️ Warning: Payment charged but failed to record in DB/QB:', webhookError);
          // Don't throw - the charge succeeded, just log the error
        }

        // Success!
        onPaymentSuccess({
          success: true,
          message: 'Payment processed successfully using saved card',
          transaction: chargeResponse.data.transaction
        });
        onClose();
        return;
      }

     // 🔧 ENHANCED: Prepare payment payload with tax allocation data
const payload = {
  orderNo: orderNo,
  paymentMethod: paymentData.method,
  user: currentUsername || 'DiniV2System',
  
  // 🆕 NEW: Israeli user context
   isIsraelUser: isUserInIsrael,
    userLocation: currentUserData?.UserLocation || 'Unknown',
  
  // Base payment data
  amount: taxAllocation.baseAmount,
  baseAmount: taxAllocation.baseAmount,
  
  // Tax allocation data
  taxOnPayment: taxAllocation.taxOnPayment,
  hasTaxAllocation: taxAllocation.hasTaxAllocation,
  
  // Fee and total data
  cardFee: cardFee, // This will be 0 for Israeli users
  totalCharge: totalCharge,
  
  // Other data
  notes: paymentData.notes,
  cardLast4: paymentData.cardLast4,
  checkNumber: paymentData.checkNumber,
  currentBalance: currentBalance,
  timestamp: getCurrentTimestamp(),
  
  // Enhanced business rules
  businessRules: {
    feeApplied: cardFee > 0,
    feeRate: isUserInIsrael ? 0 : 0.03, // 🆕 NEW: 0% for Israeli users
    feeThreshold: 150,
    
    // 🆕 NEW: Location-specific rules
    isIsraelUser: isUserInIsrael,
    requiresQuickBooks: false,
    processingRegion: isUserInIsrael ? 'Israel' : 'International',
    
    // Tax allocation context
    taxAllocationApplied: taxAllocation.hasTaxAllocation,
    taxRate: orderTaxInfo.taxRate,
    originalOrderType: orderTaxInfo.originalOrderType,
    remainingTaxBalance: orderTaxInfo.remainingTaxBalance,
    taxCalculationMethod: 'proportional_to_payment',
    orderTypeSource: 'warranty_endpoint'
  }
};
  
      console.log('🚀 Sending enhanced payment to n8n webhook:', payload);
      // Replace your getN8nWebhookUrl function with this:

      const getN8nWebhookUrl = () => {
        if (typeof window === 'undefined') return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging/webhook/payment-processing';
        
        const hostname = window.location.hostname;
        
        console.log('🔍 Detecting environment from hostname:', hostname);
        
        // Production deployment - route through CF Worker (NO /staging prefix)
        if (hostname ===  'dinitracker.com' || hostname === 'dinitracker-v2-prod.pages.dev') {
          return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/webhook/prod-payment-processing';
        }
        
        // Staging deployment - route through CF Worker (WITH /staging prefix)
        return 'https://diniwigtracker-api.super-bread-aa95.workers.dev/staging/webhook/payment-processing';
      };
      
      // Also remove the debug CF Worker test code since we're bypassing it
      // Just keep the main webhook call:
      
      const N8N_WEBHOOK_URL = getN8nWebhookUrl();
      
      console.log('🚀 Sending payment directly to n8n webhook:', N8N_WEBHOOK_URL);
      
      
      const response = await axios.post(N8N_WEBHOOK_URL, payload, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      console.log('📡 n8n response:', response.data);

      // Handle responses same as before
      if (response.data.message === "Workflow was started") {
        console.log('✅ Workflow started successfully, processing payment...');

        // 🆕 NEW: Check if warranty should be offered
        const newBalance = currentBalance - totalCharge;
        const willPayInFull = newBalance <= 0.01;
        const isNewWigOrder = ['New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderTaxInfo.originalOrderType));

        console.log('🔍 Warranty eligibility check:', {
          willPayInFull,
          isNewWigOrder,
          currentBalance,
          totalCharge,
          newBalance,
          orderType: orderTaxInfo.originalOrderType,
          workOrder: workOrder
        });

        // Check if warranty reset should be offered
        if (willPayInFull && isNewWigOrder) {
          // Show modal when balance is paid off on New Wig orders
          // Warranties are given automatically (1 year) at order creation
          // This modal offers a 9-month warranty reset (max 1 reset per barcode)
          console.log('✅ Order eligible for warranty reset - showing confirmation modal');

          setWarrantyModalData({
            orderNo: orderNo,
            customerName: workOrder?.customerName || 'Customer',
            orderType: orderTaxInfo.originalOrderType,
            payment: {
              orderNo: orderNo,
              method: paymentData.method,
              baseAmount: taxAllocation.baseAmount,
              taxOnPayment: taxAllocation.taxOnPayment,
              totalCharge: totalCharge,
              timestamp: getCurrentTimestamp()
            }
          });

          setShowWarrantyModal(true);

          // Don't close yet - warranty modal will handle that
          onPaymentSuccess({
            success: true,
            message: `Payment submitted successfully${taxAllocation.hasTaxAllocation ? ' with tax allocation' : ''}`,
            payment: {
              orderNo: orderNo,
              method: paymentData.method,
              baseAmount: taxAllocation.baseAmount,
              taxOnPayment: taxAllocation.taxOnPayment,
              totalCharge: totalCharge,
              timestamp: getCurrentTimestamp()
            }
          });
          return;
        }

        // Normal flow - no warranty needed
        onPaymentSuccess({
          success: true,
          message: `Payment submitted successfully${taxAllocation.hasTaxAllocation ? ' with tax allocation' : ''}`,
          payment: {
            orderNo: orderNo,
            method: paymentData.method,
            baseAmount: taxAllocation.baseAmount,
            taxOnPayment: taxAllocation.taxOnPayment,
            totalCharge: totalCharge,
            timestamp: getCurrentTimestamp()
          }
        });
        onClose();
        return;
      }
  
      if (response.data.success) {
        console.log('✅ Payment processed successfully:', response.data);

        // 🆕 NEW: Check if warranty reset should be offered
        const newBalance = currentBalance - totalCharge;
        const willPayInFull = newBalance <= 0.01;
        const isNewWigOrder = ['New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderTaxInfo.originalOrderType));

        console.log('🔍 Warranty eligibility check (success path):', {
          willPayInFull,
          isNewWigOrder,
          currentBalance,
          totalCharge,
          newBalance,
          orderType: orderTaxInfo.originalOrderType
        });

        if (willPayInFull && isNewWigOrder) {
          console.log('✅ Order eligible for warranty reset - showing confirmation modal');

          setWarrantyModalData({
            orderNo: orderNo,
            customerName: workOrder?.customerName || 'Customer',
            orderType: orderTaxInfo.originalOrderType,
            payment: {
              orderNo: orderNo,
              method: paymentData.method,
              baseAmount: taxAllocation.baseAmount,
              taxOnPayment: taxAllocation.taxOnPayment,
              totalCharge: totalCharge,
              timestamp: getCurrentTimestamp()
            }
          });

          setShowWarrantyModal(true);

          onPaymentSuccess({
            ...response.data,
            message: response.data.message
          });
          return;
        }

        onPaymentSuccess({
          ...response.data,
          message: response.data.message
        });
        onClose();
      } else if (response.data.success === false) {
        throw new Error(response.data.message || 'Payment processing failed');
      } else {
        console.log('✅ Payment submitted (unknown response format):', response.data);

        // 🆕 NEW: Check if warranty reset should be offered
        const newBalance = currentBalance - totalCharge;
        const willPayInFull = newBalance <= 0.01;
        const isNewWigOrder = ['New Wig', 'New Sale Wig'].includes(normalizeOrderType(orderTaxInfo.originalOrderType));

        console.log('🔍 Warranty eligibility check (unknown path):', {
          willPayInFull,
          isNewWigOrder,
          currentBalance,
          totalCharge,
          newBalance,
          orderType: orderTaxInfo.originalOrderType
        });

        if (willPayInFull && isNewWigOrder) {
          console.log('✅ Order eligible for warranty reset - showing confirmation modal');

          setWarrantyModalData({
            orderNo: orderNo,
            customerName: workOrder?.customerName || 'Customer',
            orderType: orderTaxInfo.originalOrderType,
            payment: {
              orderNo: orderNo,
              method: paymentData.method,
              baseAmount: taxAllocation.baseAmount,
              taxOnPayment: taxAllocation.taxOnPayment,
              totalCharge: totalCharge,
              timestamp: getCurrentTimestamp()
            }
          });

          setShowWarrantyModal(true);

          onPaymentSuccess({
            success: true,
            message: `Payment submitted successfully`,
            data: response.data
          });
          return;
        }

        onPaymentSuccess({
          success: true,
          message: `Payment submitted successfully`,
          data: response.data
        });
        onClose();
      }
  
      } catch (error) {
        console.error('❌ Payment processing error:', error);

        let errorMessage = 'Failed to process payment';

        if (error.code === 'ECONNREFUSED') {
          errorMessage = 'Cannot connect to payment processor. Please try again later.';
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        {
          setError({
            message: errorMessage,
            type: 'general_error'
          });
        }

      } finally {
        setIsProcessing(false);
      }
    };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setPaymentData({ ...paymentData, amount: value });
    }
  };

  // 🆕 NEW: Warranty modal handlers
  const handleWarrantyStarted = (result) => {
    console.log('✅ Warranty started from modal:', result);
    setShowWarrantyModal(false);
    onClose();
  };

  const handleWarrantySkipped = () => {
    console.log('⏭️ User skipped warranty start');
    setShowWarrantyModal(false);
    onClose();
  };

  // Show loading state while fetching tax info
  if (orderTaxInfo.loading) {
    return (
      <div className="payment-modal-overlay">
        <div className="payment-modal-content">
          <div className="payment-modal-header">
            <h5 className="payment-modal-title">Loading Payment Information...</h5>
            <button type="button" onClick={onClose} className="payment-modal-close">×</button>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#007bff' }}></i>
            <p style={{ marginTop: '10px' }}>Loading order tax information...</p>
            <p style={{ fontSize: '12px', color: '#666' }}>Checking warranty and order details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showWarrantyModal && warrantyModalData && (
        <WarrantyConfirmationModal
          orderNo={warrantyModalData.orderNo}
          customerName={warrantyModalData.customerName}
          orderType={warrantyModalData.orderType}
          onConfirm={handleWarrantyStarted}
          onCancel={handleWarrantySkipped}
          onWarrantyStarted={handleWarrantyStarted}
        />
      )}

      <div className="payment-modal-overlay">
        <div className="payment-modal-content">
        <div className="payment-modal-header">
          <h5 className="payment-modal-title">
            <i className="fas fa-credit-card payment-icon"></i>
            Add Payment - Order #{orderNo}
            {orderTaxInfo.isTaxable && (
              <span style={{ fontSize: '12px', color: '#28a745', marginLeft: '10px' }}>
                (Taxable Order - {orderTaxInfo.taxRate}%)
              </span>
            )}
          </h5>
          <button 
            type="button"
            onClick={onClose}
            className="payment-modal-close"
          >
            ×
          </button>
        </div>

        {currentBalance && (
          <div className="payment-balance-alert">
            <i className="fas fa-info-circle mr-2"></i>
            <strong>Current Balance Due: ${parseFloat(currentBalance).toFixed(2)}</strong>
            {orderTaxInfo.isTaxable && orderTaxInfo.remainingTaxBalance > 0 && (
              <div style={{ fontSize: '12px', marginTop: '5px' }}>
                Includes ${orderTaxInfo.remainingTaxBalance.toFixed(2)} tax balance
              </div>
            )}
          </div>
        )}

        {orderTaxInfo.error && (
          <div className="payment-error-alert">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {orderTaxInfo.error}
          </div>
        )}

        {error && (
          <div className="payment-error-alert">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {typeof error === 'string' ? error : error.message || 'An error occurred'}
          </div>
        )}

        <form onSubmit={handleSubmit} className="payment-form">

          {/* Saved Cards — clickable slots */}
          {canAccessCloverFeature && cloverCards.length > 0 && (
            <div className="payment-form-group">
              <label className="payment-label" style={{ marginBottom: '8px' }}>
                <i className="fas fa-credit-card" style={{ marginRight: '6px', color: '#007bff' }}></i>
                Saved Cards
              </label>

              {cloverCards.map(card => {
                const isSelected = selectedCardId === card.PaymentMethodId;
                const expired = !!card.IsExpired;
                return (
                  <div
                    key={card.PaymentMethodId}
                    onClick={() => {
                      if (expired) return;
                      if (isSelected) {
                        setSelectedCardId(null);
                        setPaymentData(prev => ({ ...prev, method: 'Cash' }));
                      } else {
                        setSelectedCardId(card.PaymentMethodId);
                        setPaymentData(prev => ({ ...prev, method: 'Credit Card' }));
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 14px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      border: `2px solid ${isSelected ? '#007bff' : 'var(--border-color, #444)'}`,
                      backgroundColor: isSelected ? 'rgba(0,123,255,0.08)' : 'var(--dark-bg)',
                      cursor: expired ? 'not-allowed' : 'pointer',
                      opacity: expired ? 0.5 : 1,
                      transition: 'border-color 0.15s, background-color 0.15s'
                    }}
                  >
                    <i className="fas fa-credit-card" style={{ fontSize: '18px', color: isSelected ? '#007bff' : '#888', flexShrink: 0 }}></i>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', fontSize: '16px' }}>
                        {card.CardBrand} •••• {card.Last4}
                        <span style={{ marginLeft: '6px', backgroundColor: card.IsDebit ? '#6f42c1' : '#007bff', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '13px', fontWeight: 'bold' }}>
                          {card.IsDebit ? 'DEBIT' : 'CREDIT'}
                        </span>
                        {expired && (
                          <span style={{ marginLeft: '4px', backgroundColor: '#dc3545', color: 'white', padding: '1px 5px', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold' }}>EXPIRED</span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                        Expires {card.ExpMonth}/{card.ExpYear}
                      </div>
                    </div>
                    {isSelected && (
                      <i className="fas fa-check-circle" style={{ color: '#007bff', fontSize: '18px', flexShrink: 0 }}></i>
                    )}
                  </div>
                );
              })}

              {selectedCardId && isSelectedCardDebit && (
                <div style={{ fontSize: '12px', color: '#28a745', marginBottom: '6px' }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                  Debit card — no processing fee applies.
                </div>
              )}
              {selectedCardId && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCardId(null);
                    setPaymentData(prev => ({ ...prev, method: 'Cash' }));
                  }}
                  style={{ background: 'none', border: 'none', color: '#888', fontSize: '12px', cursor: 'pointer', padding: '2px 0', marginBottom: '4px' }}
                >
                  <i className="fas fa-times" style={{ marginRight: '4px' }}></i>
                  Use a different payment method
                </button>
              )}
            </div>
          )}


          {!selectedCardId && (
            <div className="payment-form-group">
              <label className="payment-label">
                Payment Method <span className="payment-required">*</span>
              </label>
              <select
                className="payment-select"
                value={paymentData.method}
                onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                required
              >
                {paymentMethods.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
          )}

          <div className="payment-form-group">
            <label className="payment-label">
              Payment Amount <span className="payment-required">*</span>
            </label>
            <div className="payment-amount-container">
              <span className="payment-currency-symbol">$</span>
              <input
                type="text"
                className="payment-amount-input"
                value={paymentData.amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* 🆕 NEW: Tax Allocation Display */}
          {orderTaxInfo.isTaxable && taxAllocation.hasTaxAllocation && userEnteredAmount > 0 && (
            <div className="payment-fee-display">
              <div className="payment-fee-notice" style={{ backgroundColor: '#e8f5e8', borderColor: '#28a745' }}>
                <i className="fas fa-calculator mr-2"></i>
                <span>Payment includes {orderTaxInfo.taxRate}% tax (will be recorded separately)</span>
              </div>
              <div className="payment-fee-breakdown">
                <div className="fee-line">
                  <span>Amount Entered:</span>
                  <span>{formatCurrency(userEnteredAmount)}</span>
                </div>
                <div className="fee-line" style={{ color: '#666', fontSize: '13px' }}>
                  <span>&nbsp;&nbsp;→ Towards Order Items:</span>
                  <span>{formatCurrency(taxAllocation.baseAmount)}</span>
                </div>
                <div className="fee-line" style={{ color: '#666', fontSize: '13px' }}>
                  <span>&nbsp;&nbsp;→ Towards Tax ({orderTaxInfo.taxRate}%):</span>
                  <span>{formatCurrency(taxAllocation.taxOnPayment)}</span>
                </div>
                {cardFee > 0 && (
                  <div className="fee-line">
                    <span>Processing Fee (3%):</span>
                    <span>{formatCurrency(cardFee)}</span>
                  </div>
                )}
                {cardFee > 0 && (
                  <div className="fee-line fee-total">
                    <span><strong>Total to Charge:</strong></span>
                    <span><strong>{formatCurrency(totalCharge)}</strong></span>
                  </div>
                )}
              </div>
            </div>
          )}

{/* Credit Card Fee Display (when no tax allocation) */}
{!taxAllocation.hasTaxAllocation && paymentData.method === 'Credit Card' && userEnteredAmount >= 150 && !isUserInIsrael && cardFee > 0 && (
  <div className="payment-fee-display">
    <div className="payment-fee-notice">
      <i className="fas fa-info-circle mr-2"></i>
      <span>3% processing fee applies to card payments over $150</span>
    </div>
    <div className="payment-fee-breakdown">
      <div className="fee-line">
        <span>Payment Amount:</span>
        <span>${formatCurrency(userEnteredAmount)}</span>
      </div>
      <div className="fee-line">
        <span>Processing Fee (3%):</span>
        <span>${formatCurrency(cardFee)}</span>
      </div>
      <div className="fee-line fee-total">
        <span><strong>Total to Charge:</strong></span>
        <span><strong>${formatCurrency(totalCharge)}</strong></span>
      </div>
    </div>
  </div>
)}

{/* Israeli User Message */}
{isUserInIsrael && paymentData.method === 'Credit Card' && userEnteredAmount >= 150 && (
  <div className="payment-fee-display">
    <div className="payment-fee-notice" style={{ backgroundColor: '#1bb9eeff', borderColor: '#17a2b8' }}>
      <i className="fas fa-globe mr-2"></i>
      <span>No processing fees for Israeli users</span>
    </div>
  </div>
)}
          {/* {paymentData.method.includes('Card') && (
            <div className="payment-form-group">
              <label className="payment-label">
                Card Last 4 Digits
              </label>
              <input
                type="text"
                className="payment-input"
                value={paymentData.cardLast4}
                onChange={(e) => setPaymentData({ ...paymentData, cardLast4: e.target.value.slice(0, 4) })}
                placeholder="1234"
                maxLength={4}
              />
            </div>
          )}

          {paymentData.method === 'Check' && (
            <div className="payment-form-group">
              <label className="payment-label">
                Check Number
              </label>
              <input
                type="text"
                className="payment-input"
                value={paymentData.checkNumber}
                onChange={(e) => setPaymentData({ ...paymentData, checkNumber: e.target.value })}
                placeholder="Check #"
              />
            </div>
          )}
      
          */}
          <div className="payment-form-group">
            <label className="payment-label">
              Notes
            </label>
            <textarea
              className="payment-textarea"
              value={paymentData.notes}
              onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
              placeholder="Optional payment notes..."
              rows={3}
            />
          </div> 
    

          <div className="payment-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="payment-btn payment-btn-secondary"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="payment-btn payment-btn-primary"
              disabled={isProcessing || !paymentData.amount}
            >
              {isProcessing ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Processing...
                </>
              ) : (canAccessCloverFeature && selectedCardId) ? (
                <>
                  <i className="fas fa-credit-card mr-2"></i>
                  Charge Saved Card ${formatCurrency(totalCharge || userEnteredAmount)}
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-2"></i>
                  {cardFee > 0 ?
                    `Charge $${formatCurrency(totalCharge)}` :
                    'Process Payment'
                  }
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default PaymentModal;